import React, { useMemo } from 'react';
import EstimatItemRowCustomer from './EstimatItemRowCustomer';
import EtableringRow from './EtableringRow';
import { calculateItemTotal, calculateItemPricesWithBaspaket } from '../../domain/calculations';
import { radrubrikFor } from '../../utils/kategoriRubriker';

/**
 * EstimatCategoryTable - Renderar en hel kategori-tabell för estimat (KUNDVY)
 *
 * Hanterar både standard kategorier och specialfall (byggoffert med subcategories + etablering)
 * Visar priser INKLUSIVE proportionellt fördelat baspaket så summeringarna stämmer överens
 *
 * 🚀 OPTIMERING: Använder useMemo för att cacha beräkningar
 */
const EstimatCategoryTable = ({
  categoryKey,
  categoryData,
  hideHeader = false,
  // Props för kommentarer
  comments,
  commentsRaw,
  estimatData,
  showComments,
  toggleCommentField,
  handleCommentChange,
  handleSaveComment,
  handleDeleteComment,
  handleSaveByggkonsultComment,
  handleEditByggkonsultEntry,
  isCustomerView,
  // Tidigare version i läsläge — inga kommentarer att skriva
  lasLage = false,
  hasUnsavedChanges,
  isSavedLocally,
  logActivity,
}) => {
  /**
   * Spärrarna låg tidigare HÄR, före hookarna. Antalet hooks måste vara
   * detsamma i varje render — en tidig return före ett useMemo gör att React
   * ser tre hooks ibland och noll andra gånger, och kastar "Rendered fewer
   * hooks than expected". Båda ligger nu nedanför de tre memona i stället.
   */
  // 🚀 OPTIMERING: Cacha selectedItems så filter inte körs på varje render
  // Sortera på sortOrder så ordningsändringar i BK-vyn speglas till kundvyn.
  // OBS: vi släpper även igenom items med quantity 0 — dessa renderas som
  // "preliminärt" i raden men räknas inte med i totaler (price = 0 ändå).
  const selectedItems = useMemo(
    () => Object.entries(categoryData || {})
      .filter(([_, item]) => item && item.selected)
      .sort(([, a], [, b]) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)),
    [categoryData]
  );


  // 🎯 BERÄKNA JUSTERADE PRISER INKLUSIVE BASPAKET-FÖRDELNING
  // Detta säkerställer att rad-priser + summering stämmer överens
  const adjustedPrices = useMemo(() => {
    return calculateItemPricesWithBaspaket(
      estimatData?.estimatKategorier,
      estimatData?.projectInfo,
      estimatData?.baspaketAndelar
    );
  }, [estimatData?.estimatKategorier, estimatData?.projectInfo, estimatData?.baspaketAndelar]);

  // 🚀 OPTIMERING: Cacha totalsummor (nu baserade på justerade priser)
  const totals = useMemo(() => {
    // Summera justerade priser för denna kategori
    const categoryTotal = selectedItems.reduce((sum, [itemName]) => {
      const key = `${categoryKey}_${itemName}`;
      return sum + (adjustedPrices[key] || 0);
    }, 0);

    // Etablering (med baspaket inkluderat)
    const etableringAdminTotal = adjustedPrices['byggoffert_etablering'] || 0;

    return {
      categoryTotal,
      etableringAdminTotal,
    };
  }, [selectedItems, adjustedPrices, categoryKey]);

  // Spärrarna, nu efter samtliga hooks
  if (!categoryData || typeof categoryData !== 'object') return null;
  if (selectedItems.length === 0) return null;

  const categoryNames = {
    projektering: 'Projektering',
    byggoffert: 'Byggoffert generalentreprenör',
    sidoentreprenorer: 'Sidoentreprenörer/specialister',
    inkopFastInredning: 'Inköp fast inredning'
  };

  const radrubrik = radrubrikFor(categoryKey);

  return (
    <div key={categoryKey} className="mb-6">
      {!hideHeader && (
        <h3 className="text-sm sm:text-lg font-semibold text-white mb-3 bg-gray-600 px-2 sm:px-3 py-1.5 sm:py-2 rounded">
          {categoryNames[categoryKey] || categoryKey}
        </h3>
      )}
      <div className="overflow-hidden rounded border border-gray-200">
        <table className="w-full table-fixed">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left text-sm font-medium text-gray-700 w-1/2">{radrubrik}</th>
              <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-right text-sm font-medium text-gray-700 w-1/2 sm:w-1/4">Summa</th>
              <th className="p-0 sm:px-4 sm:py-2 text-center text-sm font-medium text-gray-700 w-0 max-w-0 sm:max-w-none sm:w-1/4 overflow-hidden">
                <span className="hidden sm:inline">{lasLage ? '' : 'Kommentar'}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {categoryKey === 'byggoffert' ? (
              // Special handling for byggoffert with subcategories
              <React.Fragment>
                {selectedItems.map(([itemName, item]) => {
                  // Determine display name and subcategory status
                  const isSubcategoryItem = itemName.includes('_');
                  let displayName;

                  if (isSubcategoryItem) {
                    const [subcategoryName, actualItemName] = itemName.split('_', 2);
                    const displayNameFromKey = subcategoryName === 'invändigt' || subcategoryName === 'utvändigt'
                      ? actualItemName
                      : itemName;
                    displayName = (typeof item.label === 'string' && item.label.trim()) ? item.label : displayNameFromKey;
                  } else {
                    displayName = (typeof item.label === 'string' && item.label.trim()) ? item.label : itemName;
                  }

                  const commentKey = `${categoryKey}_${itemName}`;
                  // Använd justerat pris inkl baspaket-andel
                  const adjustedPrice = adjustedPrices[commentKey] || calculateItemTotal(item);

                  return (
                    <EstimatItemRowCustomer
                      key={`${categoryKey}_${itemName}`}
                      displayName={displayName}
                      item={item}
                      categoryKey={categoryKey}
                      itemName={itemName}
                      commentKey={commentKey}
                      comments={comments}
                      commentsRaw={commentsRaw}
                      estimatData={estimatData}
                      showComments={showComments}
                      toggleCommentField={toggleCommentField}
                      handleCommentChange={handleCommentChange}
                      handleSaveComment={handleSaveComment}
                      handleDeleteComment={handleDeleteComment}
                      handleSaveByggkonsultComment={handleSaveByggkonsultComment}
                      handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                      isCustomerView={isCustomerView}
                      lasLage={lasLage}
                      hasUnsavedChanges={hasUnsavedChanges}
                      isSavedLocally={isSavedLocally}
                      logActivity={logActivity}
                      isSubcategoryItem={isSubcategoryItem}
                      adjustedPrice={adjustedPrice}
                    />
                  );
                })}

                {/* Etablering-rad (specialkomponent) */}
                <EtableringRow
                  categoryKey={categoryKey}
                  etableringAdminTotal={totals.etableringAdminTotal}
                  comments={comments}
                  commentsRaw={commentsRaw}
                  estimatData={estimatData}
                  showComments={showComments}
                  toggleCommentField={toggleCommentField}
                  handleCommentChange={handleCommentChange}
                  handleSaveComment={handleSaveComment}
                  handleDeleteComment={handleDeleteComment}
                  handleSaveByggkonsultComment={handleSaveByggkonsultComment}
                  handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                  isCustomerView={isCustomerView}
                      lasLage={lasLage}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSavedLocally={isSavedLocally}
                  logActivity={logActivity}
                />

                {/* Total summa rad för byggoffert (inkl baspaket-fördelning OCH
                    etablering/avfall/admin — etablering ingår nu i Summa byggoffert) */}
                {(() => {
                  const summaByggoffert = (totals.categoryTotal || 0) + (totals.etableringAdminTotal || 0);
                  return (
                <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 font-bold">Summa byggoffert</td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 text-right text-sm text-gray-700">
                    {summaByggoffert > 0 ? `${summaByggoffert.toLocaleString('sv-SE')} kr` : '-'}
                  </td>
                  <td className="p-0 sm:px-4 sm:py-3 text-center text-sm w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
                    <span className="hidden sm:inline">-</span>
                  </td>
                </tr>
                  );
                })()}
              </React.Fragment>
            ) : (
              // Standard handling for other categories
              <React.Fragment>
                {selectedItems.map(([itemName, item]) => {
                  const commentKey = `${categoryKey}_${itemName}`;
                  const displayName = (typeof item.label === 'string' && item.label.trim()) ? item.label : itemName;
                  // Använd justerat pris inkl baspaket-andel
                  const adjustedPrice = adjustedPrices[commentKey] || calculateItemTotal(item);

                  return (
                    <EstimatItemRowCustomer
                      key={`${categoryKey}_${itemName}`}
                      displayName={displayName}
                      item={item}
                      categoryKey={categoryKey}
                      itemName={itemName}
                      commentKey={commentKey}
                      comments={comments}
                      commentsRaw={commentsRaw}
                      estimatData={estimatData}
                      showComments={showComments}
                      toggleCommentField={toggleCommentField}
                      handleCommentChange={handleCommentChange}
                      handleSaveComment={handleSaveComment}
                      handleDeleteComment={handleDeleteComment}
                      handleSaveByggkonsultComment={handleSaveByggkonsultComment}
                      handleEditByggkonsultEntry={handleEditByggkonsultEntry}
                      isCustomerView={isCustomerView}
                      lasLage={lasLage}
                      hasUnsavedChanges={hasUnsavedChanges}
                      isSavedLocally={isSavedLocally}
                      logActivity={logActivity}
                      isSubcategoryItem={false}
                      adjustedPrice={adjustedPrice}
                    />
                  );
                })}

                {/* Summeringsrader för vanliga kategorier (använder justerade totaler) */}
                {(categoryKey === 'projektering' || categoryKey === 'inkopFastInredning' || categoryKey === 'sidoentreprenorer') && selectedItems.length > 0 && (() => {
                  let summaText = '';
                  if (categoryKey === 'projektering') summaText = 'Summa projektering';
                  else if (categoryKey === 'inkopFastInredning') summaText = 'Summa fast inredning';
                  else if (categoryKey === 'sidoentreprenorer') summaText = 'Summa sidoentreprenörer';

                  return (
                    <tr className="bg-gray-100 border-t-2 border-gray-300 font-semibold">
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 font-bold">{summaText}</td>
                      <td className="px-2 sm:px-4 py-2 sm:py-3 text-right text-sm font-bold text-gray-700">
                        {totals.categoryTotal.toLocaleString('sv-SE')} kr
                      </td>
                      <td className="p-0 sm:px-4 sm:py-3 text-center text-sm w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
                        <span className="hidden sm:inline">-</span>
                      </td>
                    </tr>
                  );
                })()}
              </React.Fragment>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EstimatCategoryTable;

